import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Input } from "@/pages/MemberAuth";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset Password" subtitle="We'll email you a reset link.">
      {sent ? (
        <div data-testid="forgot-sent" className="bg-white p-8 text-center">
          <MailCheck className="w-14 h-14 text-[#D90429] mx-auto mb-5" />
          <h2 className="font-display text-2xl uppercase text-[#0A1128]">Check Your Email</h2>
          <p className="text-slate-600 mt-3">
            If an account exists for <span className="font-semibold">{email}</span>, a password reset
            link is on its way. The link expires in 1 hour.
          </p>
          <Link to="/login" className="inline-block mt-6 text-[#D90429] font-semibold hover:underline">Back to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form" className="bg-white p-8">
          <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Email</label>
          <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send Reset Link"}
          </button>
          <p className="text-center text-sm text-slate-500 mt-5">
            Remembered it? <Link to="/login" className="text-[#D90429] font-semibold hover:underline">Log in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid Link" subtitle="This reset link is missing or broken.">
        <div className="bg-white p-8 text-center">
          <p className="text-slate-600">Please request a new password reset link.</p>
          <Link to="/forgot-password" className="inline-block mt-5 text-[#D90429] font-semibold hover:underline">Request new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="New Password" subtitle="Choose a strong new password.">
      <form onSubmit={submit} data-testid="reset-password-form" className="bg-white p-8">
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">New Password</label>
        <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" required />
        <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Confirm Password</label>
        <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : "Update Password"}
        </button>
      </form>
    </AuthShell>
  );
}
